const asObject = (value) => value && typeof value === "object" && !Array.isArray(value) ? value : {};
const asArray = (value) => Array.isArray(value) ? value : [];
const text = (value, fallback = "") => typeof value === "string" && value.trim() ? value.trim() : fallback;
export const clampPercent = (value) => Math.max(0, Math.min(100, Number.isFinite(Number(value)) ? Number(value) : 0));

export function scheduledAssessmentsEnabled(features) {
  const value = asObject(features);
  if (typeof value.scheduledAssessments === "boolean") {
    return value.scheduledAssessments;
  }
  // Compatibility for a frontend released before the capability split. The
  // bridge-aware backend always sends an explicit false value.
  return value.assessments === true;
}

export function learnerResourceLibraryEnabled(features) {
  const value = asObject(features);
  // Compatibility for shells cached before this capability was introduced.
  // The bridge-aware backend always emits an explicit false value.
  return value.resourceLibrary !== false;
}

function normalizeAssessmentGates(raw) {
  const source = asObject(raw);
  const units = Object.fromEntries(Object.entries(asObject(source.units)).map(
    ([unitKey, unitValue]) => [unitKey, {
      outcomes: Object.fromEntries(Object.entries(asObject(asObject(unitValue).outcomes)).map(
        ([outcomeKey, outcomeValue]) => [outcomeKey, {
          sections: Object.fromEntries(Object.entries(asObject(asObject(outcomeValue).sections)).map(
            ([sectionKey, sectionValue]) => {
              const gate = asObject(sectionValue);
              return [sectionKey, {
                ordinal: Number.isFinite(Number(gate.ordinal)) ? Number(gate.ordinal) : 0,
                required: gate.required !== false,
                ...(gate.priorLearning === true ? { priorLearning: true } : {}),
                available: gate.available === true,
                passed: gate.passed === true,
                locked: gate.locked === true,
                state: text(gate.state, gate.passed ? "passed" : (gate.available ? "available" : "locked")),
              }];
            },
          )),
        }],
      )),
    }],
  ));
  if (!Object.keys(units).length) return null;
  return {
    version: Number(source.version) || 1,
    passPercent: clampPercent(source.passPercent ?? 60),
    units,
  };
}

const initials = (name) => text(name, "Learner")
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase())
  .join("");

export function normalizeLearningWindow(raw) {
  const value = asObject(raw);
  return {
    configRevision: Math.max(0, Number(value.configRevision) || 0),
    configured: value.configured === true,
    phase: text(value.phase, "unscheduled"),
    learningOpen: value.learningOpen !== false,
    startDate: text(value.startDate) || null,
    endDate: text(value.endDate) || null,
    timeZone: text(value.timeZone, "Africa/Johannesburg"),
    calendarMode: text(value.calendarMode) || null,
    todayKey: text(value.todayKey) || null,
    daysUntilStart: value.daysUntilStart != null && Number.isFinite(Number(value.daysUntilStart)) ?
      Number(value.daysUntilStart) : null,
    daysUntilEnd: value.daysUntilEnd != null && Number.isFinite(Number(value.daysUntilEnd)) ?
      Number(value.daysUntilEnd) : null,
  };
}

function normalizePacing(raw) {
  const value = asObject(raw);
  const guidance = asObject(value.guidance);
  return {
    window: normalizeLearningWindow(value.window),
    guidance: {
      state: text(guidance.state, "unavailable"),
      actualPercent: Number.isFinite(Number(guidance.actualPercent)) ?
        clampPercent(guidance.actualPercent) : null,
      expectedPercent: Number.isFinite(Number(guidance.expectedPercent)) ?
        clampPercent(guidance.expectedPercent) : null,
      deltaPercent: Number.isFinite(Number(guidance.deltaPercent)) ?
        Number(guidance.deltaPercent) : null,
      basis: text(guidance.basis) || null,
    },
  };
}

export function normalizeLearnerShell(raw) {
  const input = asObject(raw);
  const sourceFeatures = asObject(input.features);
  const sourceProfile = asObject(input.profile);
  const profile = {
    displayName: text(sourceProfile.displayName, "Learner"),
    profilePicUrl: text(sourceProfile.profilePicUrl),
    studentNumber: text(sourceProfile.studentNumber),
    phone: text(sourceProfile.phone || sourceProfile.mobileNumber),
    email: text(sourceProfile.email),
  };
  profile.initials = initials(profile.displayName);

  const intakes = asArray(input.intakes).map((item) => {
    const value = asObject(item);
    const rawSchedule = asObject(value.scheduleSummary);
    return {
      id: text(value.id),
      name: text(value.name, "Learning intake"),
      programId: text(value.programId),
      startDate: text(value.startDate),
      endDate: text(value.endDate),
      status: text(value.status, "active"),
      studyType: text(value.studyType),
      campus: text(value.campus),
      scheduleSummary: {
        itemCount: Math.max(0, Number(rawSchedule.itemCount) || 0),
        firstDate: text(rawSchedule.firstDate || rawSchedule.nextDate),
        lastDate: text(rawSchedule.lastDate),
      },
      facilitator: asObject(value.facilitator),
      learningWindow: normalizeLearningWindow(value.learningWindow),
      adaptiveLearning: normalizeAdaptiveLearning(value.adaptiveLearning),
      applicationPending: value.applicationPending === true,
    };
  }).filter((item) => item.id && item.programId);

  const programs = asArray(input.programs || input.programmes).map((item) => {
    const value = asObject(item);
    return {
      id: text(value.id),
      name: text(value.name || value.title, "Assigned programme"),
      originator: text(value.originator),
      moduleCount: Number(value.moduleCount) || 0,
      imageUrl: text(value.imageUrl),
      intakeId: text(value.intakeId),
    };
  }).filter((item) => item.id);

  const activeIntakeId = text(input.activeIntakeId) || intakes[0]?.id || "";
  const activeIntake = intakes.find((item) => item.id === activeIntakeId) || intakes[0] || null;
  const programById = new Map(programs.map((program) => [program.id, program]));
  const allocatedPrograms = intakes.map((intake) => ({
    ...(programById.get(intake.programId) || { id: intake.programId, name: intake.name }),
    intakeId: intake.id,
    intakeName: intake.name,
    intakeStatus: intake.status,
    learningWindow: intake.learningWindow,
    applicationPending: intake.applicationPending === true,
  }));

  return {
    profile,
    intakes,
    programs: allocatedPrograms.length ? allocatedPrograms : programs,
    activeIntakeId: activeIntake?.id || activeIntakeId,
    activeProgramId: activeIntake?.programId || programs[0]?.id || "",
    branding: asObject(input.branding),
    profileCompletion: input.profileCompletion,
    loadingCapabilities: {
      programSnapshotNavigation: asObject(input.loadingCapabilities).programSnapshotNavigation === true,
    },
    features: {
      assessments: false,
      badges: false,
      badgeAcknowledgement: false,
      certificates: false,
      ...sourceFeatures,
      scheduledAssessments: scheduledAssessmentsEnabled(sourceFeatures),
      resourceLibrary: learnerResourceLibraryEnabled(sourceFeatures),
    },
    facilitator: asObject(input.facilitator || activeIntake?.facilitator),
    upcomingEvents: asArray(input.upcomingEvents),
    account: asObject(input.account),
    serverTime: input.serverTime || null,
  };
}

export function normalizeAdaptiveLearning(raw) {
  const value = asObject(raw);
  const known = ["enabled", "disabled"].includes(value.state);
  const enabled = known && value.state === "enabled" && value.enabled === true;
  return {
    enabled,
    simplified: enabled && value.simplified === true,
    ...(["0", "1", "2", "base", "4", "5"].includes(value.currentComplexity) ?
      { currentComplexity: value.currentComplexity } : {}),
    state: known ? (enabled ? "enabled" : "disabled") : "unavailable",
    revision: Number.isSafeInteger(value.revision) && value.revision >= 0 ? value.revision : 0,
    policyVersion: text(value.policyVersion),
    ...(value.state === "unavailable" && value.enabled === false &&
      value.configurationEnabled === true && value.reason === "programme_range_changed" ?
      { reason: "programme_range_changed", configurationEnabled: true } : {}),
  };
}

export function normalizeProgressSnapshot(raw, navigation) {
  const input = asObject(raw);
  const nav = asObject(navigation);
  const unitValues = Array.isArray(input.units) ? input.units : Object.values(asObject(input.units));
  const navigationUnits = asArray(nav.units || nav.modules?.flatMap((module) => asArray(module.units)));
  const progressById = new Map();
  unitValues.forEach((unit) => {
    const value = asObject(unit);
    [
      value.storageKey,
      value.unitId,
      value.unitKey,
      value.id,
      ...asArray(value.storageKeys),
    ].map((key) => text(key)).filter(Boolean)
      .forEach((key) => progressById.set(key, value));
  });

  const units = (navigationUnits.length ? navigationUnits : unitValues).map((unit, index) => {
    const navUnit = asObject(unit);
    const progressUnit = progressById.get(text(navUnit.storageKey || navUnit.unitKey || navUnit.unitId || navUnit.id)) || navUnit;
    const progress = asObject(progressUnit.progress);
    return {
      id: text(navUnit.storageKey || navUnit.unitKey || navUnit.unitId || navUnit.id || progressUnit.unitId),
      number: Number(navUnit.number) || index + 1,
      title: text(navUnit.name || navUnit.title || progressUnit.unitName, `Unit ${index + 1}`),
      // State is server-owned. The browser never derives unlock/completion.
      state: text(progressUnit.state || navUnit.state, "locked"),
      progress: clampPercent(progress.percent),
      competencePercent: clampPercent(progress.competencePercent),
      competent: progress.competent === true,
      competenceState: text(progressUnit.competenceState),
      ...(progressUnit.priorLearning?.recognised ? { priorLearning: progressUnit.priorLearning } : {}),
      assessmentGrades: asArray(progressUnit.assessmentGrades).map((grade) => {
        const value = asObject(grade);
        return {
          outcomeId: text(value.outcomeId),
          outcomeName: text(value.outcomeName),
          sectionId: text(value.sectionId),
          title: text(value.title),
          written: value.written === true,
          ...(value.priorLearning === true ? { priorLearning: true, scope: value.scope,
            source: text(value.source) } : {}),
          passed: value.passed === true,
          score: value.score !== null && value.score !== undefined && Number.isFinite(Number(value.score)) ? Number(value.score) : null,
          maxScore: value.maxScore !== null && value.maxScore !== undefined && Number.isFinite(Number(value.maxScore)) ? Number(value.maxScore) : null,
          percent: value.percent === null || value.percent === undefined ? null : clampPercent(value.percent),
          completedAt: value.completedAt || null,
          currentComplexity: text(value.currentComplexity),
          previousComplexity: text(value.previousComplexity),
        };
      }),
      outcomes: asArray(navUnit.outcomes).map((outcome) => text(asObject(outcome).name || asObject(outcome).title || outcome)).filter(Boolean),
      route: asObject(navUnit.route),
    };
  });

  const course = asObject(input.course);
  return {
    intakeId: text(input.intakeId),
    programId: text(input.programId),
    generatedAt: input.generatedAt || null,
    ...(input.adaptiveLearning ? { adaptiveLearning: normalizeAdaptiveLearning(input.adaptiveLearning) } : {}),
    assessmentGates: normalizeAssessmentGates(input.assessmentGates),
    pacing: normalizePacing(input.pacing),
    units,
    course: {
      totalExpected: Number(course.totalExpected) || 0,
      totalCompleted: Number(course.totalCompleted) || 0,
      progressPercent: clampPercent(course.progressPercent),
      averagePercent: clampPercent(course.averagePercent),
      complete: course.complete === true,
      totalPassed: Number(course.totalPassed) || 0,
      ...(course.totalRecognised ? { totalRecognised: Number(course.totalRecognised) } : {}),
      competencePercent: clampPercent(course.competencePercent),
      competent: course.competent === true,
      passPercent: clampPercent(course.passPercent ?? 60),
    },
  };
}

export function normalizeEngagementSummary(raw) {
  const input = asObject(raw);
  const badges = asArray(input.badges || input.awards).map((badge) => {
    const value = asObject(badge);
    const progress = asObject(value.progress);
    return {
      id: text(value.badgeId || value.id),
      awardId: text(value.awardId || value.id),
      name: text(value.name, "Learning badge"),
      description: text(value.description),
      status: text(value.status, value.earnedAt ? "earned" : "locked"),
      earnedAt: value.earnedAt || null,
      ruleVersion: text(value.ruleVersion),
      scope: asObject(value.scope),
      progress: { current: Number(progress.current) || 0, target: Math.max(1, Number(progress.target) || 1) },
      presentation: asObject(value.presentation),
    };
  }).filter((badge) => badge.id);
  return {
    enabled: input.enabled !== false,
    badges,
    newlyAwarded: asArray(input.newlyAwarded || input.newAwards).map(asObject).filter((item) => item.awardId || item.id),
    recentActivity: asArray(input.recentActivity),
    activeStudySecondsTotal: Number(input.activeStudySecondsTotal) || 0,
    smartBreak: {
      eligible: asObject(input.smartBreak).eligible === true,
      current: Number(asObject(input.smartBreak).current) || 0,
      target: Math.max(1, Number(asObject(input.smartBreak).target) || 2700),
    },
    preferences: {
      motivationalEmailEnabled:
        asObject(input.preferences).motivationalEmailEnabled !== false,
      pacingEmailEnabled:
        asObject(input.preferences).pacingEmailEnabled !== false,
      deadlineEmailEnabled:
        asObject(input.preferences).deadlineEmailEnabled !== false,
      quietHoursStart: Number(asObject(input.preferences).quietHoursStart ?? 20),
      quietHoursEnd: Number(asObject(input.preferences).quietHoursEnd ?? 7),
    },
  };
}

function normalizeJourneyItem(value, index) {
  const item = asObject(value);
  const context = asObject(item.context || item.locator);
  const activeSeconds = Number(item.activeSeconds ?? item.durationSeconds);
  return {
    id: text(item.id || item.eventId || item.sessionId, `journey-${index}`),
    type: text(item.type, "learning_activity"),
    title: text(item.title || item.label, "Learning activity"),
    description: text(item.description || item.summary),
    occurredAt: item.occurredAt || item.endedAt || item.startedAt || item.updatedAt || null,
    activeSeconds: Number.isFinite(activeSeconds) ? Math.max(0, Math.floor(activeSeconds)) : 0,
    context: {
      kind: text(context.kind || item.kind),
      unitKey: text(context.unitKey || item.unitKey),
      outcomeKey: text(context.outcomeKey || item.outcomeKey),
      sectionKey: text(context.sectionKey || item.sectionKey),
      sectionTitle: text(context.sectionTitle || item.sectionTitle),
    },
  };
}

/**
 * Tracking V2 is programme scoped. Do not fall back to the legacy account-wide
 * activeStudySecondsTotal here: that number includes visible/idle browser time
 * and can span multiple programme allocations.
 */
export function normalizeTrackingSummary(raw) {
  const response = asObject(raw);
  const input = asObject(response.summary || response.trackingSummary || response);
  const programme = asObject(input.programme || input.program || input.totals);
  const recentJourney = asArray(
    input.recentJourney || input.items || input.recentActivity || input.timeline,
  ).map(normalizeJourneyItem);
  return {
    enabled: input.enabled !== false,
    intakeId: text(input.intakeId || programme.intakeId),
    programId: text(input.programId || programme.programId),
    generatedAt: input.generatedAt || null,
    programme: {
      activeSeconds: Math.max(0, Number(programme.activeSeconds) || 0),
      contentActiveSeconds: Math.max(0, Number(programme.contentActiveSeconds) || 0),
      assessmentActiveSeconds: Math.max(0, Number(programme.assessmentActiveSeconds) || 0),
      mediaActiveSeconds: Math.max(0, Number(programme.mediaActiveSeconds) || 0),
      sessionCount: Math.max(0, Number(programme.sessionCount) || 0),
      sectionCount: Math.max(0, Number(programme.sectionCount) || 0),
    },
    recentJourney,
    sections: asArray(input.sections),
    daily: asArray(input.daily),
  };
}

export function makeProgramRoute(program) {
  const value = asObject(program);
  if (!text(value.id) || !text(value.intakeId)) return "";
  return `programs/${encodeURIComponent(value.id)}?intake=${encodeURIComponent(value.intakeId)}`;
}

export function createRequestState(status = "idle", data = null, error = "") {
  return { status, data, error };
}
